// useWidgetData — a widget's data, fetched from app-api (M2 slice 4).
//
// The contract seam (m2-plan §1): a widget fetches its endpoint through this hook
// instead of reading a synchronous local dummy-data getter.
// `useWidgetData("injuries-suspension", id)` returns exactly what that getter
// returned — `WidgetPayloadMap[key]` — so the pure shaper + the component below it
// don't change. The only new work per widget is handling `isPending` / `isError`
// (the getter was synchronous).
//
// Endpoints are fixture-scoped and return the widget's FULL dataset; controls
// shape it client-side (so toggles stay instant — no refetch). Fixture is the
// only server param in M2.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { WidgetKey, WidgetPayloadMap } from "@bento/shared";
import { apiGet } from "./api.js";
import { useBentoStore } from "../store/index.js";

// Design check: `?slow=1` in the address bar holds every widget fetch back, so
// the loading skeletons can be seen (the real API answers too fast to judge them).
const SLOW = typeof location !== "undefined" && new URLSearchParams(location.search).has("slow");
const SLOW_MS = 2500;

/**
 * Fetch widget `key`'s dataset for fixture `fixtureId` via
 * `GET /widgets/{key}?fixture={id}`. Returns the React Query result typed to
 * that widget's contract (`WidgetPayloadMap[K]`) — read `data`, `isPending`,
 * `isError`, `refetch` at the call site.
 *
 * The query is disabled until `fixtureId` is truthy (a widget may mount before
 * the fixture is known); it stays `isPending` until then — unless the fixture
 * list itself failed to load (store `fixturesError`), in which case the id will
 * never arrive, so the query runs and fails at once and the widget shows its
 * error state instead of a skeleton forever.
 */
export function useWidgetData<K extends WidgetKey>(
  key: K,
  fixtureId: string | null | undefined
): UseQueryResult<WidgetPayloadMap[K], Error> {
  const listFailed = useBentoStore((s) => s.fixturesError);
  return useQuery({
    queryKey: ["widget", key, fixtureId],
    enabled: !!fixtureId || listFailed,
    // A missing id won't appear by retrying; otherwise keep the client's setting.
    ...(fixtureId ? {} : { retry: false }),
    queryFn: async () => {
      if (!fixtureId) throw new Error("Fixture list unavailable");
      if (SLOW) await new Promise((r) => setTimeout(r, SLOW_MS));
      return apiGet<WidgetPayloadMap[K]>(
        `/widgets/${key}?fixture=${encodeURIComponent(fixtureId)}`
      );
    },
  });
}
